import { ColDef, ICellRendererParams } from '@ag-grid-community/core';
import AgGridSolid from '@ag-grid-community/solid';
import { Box, Chip, Stack } from '@atgfr8/atg-ui-tailwind';
import {
  AlertCircleIcon,
  ArrowRightIcon,
  HistoryIcon,
  MagnifyIcon,
  MinusIcon,
  RefreshIcon,
} from '@atgfr8/icons';
import { equipmentTypes } from '@common/commonLists';
import { COUNTRY_CODES } from '@constants/countryCodes';
import { NewAutoComplete as AutoComplete } from '@components/Autocomplete/AutoCompleteWrapper';
import Button from '@components/Button/Button';
import { BasicModal } from '@components/Modal/BasicModal';
import { ToastType } from '@components/Toast/Toast';
import { AtgLoader } from '@components/AtgLoader';
import { alternateRowStyle } from '@components/Grid/utils';
import { SelectInput } from '@components/Select/SelectInput';
import {
  SelectInputCustom,
  SelectInputCustomOnChangeActionType,
} from '@components/Select/SelectInputCustom';
import { fetchLaneHistoryData } from '@store/carriers/laneSearch/services';
import {
  CarrierLaneHistory,
  LaneHistorySearchPayload,
} from '@store/carriers/laneSearch/types';
import { currencyCellFormatter, dateFormatter } from '@store/loadboard/utils';
import { closeModal, isModalOpen, openModal } from '@store/modals/store';
import { customerStore } from '@store/orders/store';
import { Lane, LaneHistoryFilters, LaneLocation } from '@store/orders/types';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { ILocationSearch } from '@typeDefinitions/locationTypes';
import { createEscapeToClose } from '@utils/createEscapeToClose';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { LocationSelect } from '@views/carrierMatching/types';
import {
  CarrierDestinationData,
  CarrierOriginData,
} from '@views/carriers/laneSearch/components/LaneSearchColumns';
import { DateTime } from 'luxon';
import { createMemo, createSignal, Show } from 'solid-js';
import { createStore } from 'solid-js/store';

import {
  ALL_MODES,
  canSearch,
  DEFAULT_RADIUS_MILES,
  DEFAULT_TIME_HORIZON,
  EMPTY_LOCATION,
  formatAutocompleteValue,
  formatLocation,
  getLoadLane,
  hasLocation,
  isSamePayload,
  MODE_ITEMS,
  PAGE_SIZE,
  resolveLocationCoordinates,
  TIME_HORIZON_ITEMS,
  toLaneHistoryPayload,
  toLaneLocation,
} from './helpers';
import { RadiusSlider } from './RadiusSlider';

type PropsT = {
  load: ILoadEditViewModel;
  enabled: boolean;
};

const clearedLocation = (current: LaneLocation): LaneLocation => ({
  ...EMPTY_LOCATION,
  country: current.country,
});

const keepCoordinates = (
  next: LaneLocation,
  current: LaneLocation,
): LaneLocation =>
  next.lat === 0 &&
  next.long === 0 &&
  next.country === current.country &&
  formatAutocompleteValue(next) === formatAutocompleteValue(current)
    ? { ...next, lat: current.lat, long: current.long }
    : { ...next };

const renderLocationItem = (item: unknown) => {
  const location = item as ILocationSearch;

  return (
    <span class="text-sm">
      {`${location.city}, ${location.state} ${location.country} ${location.zipcode}`}
    </span>
  );
};

const columnDefs: ColDef[] = [
  {
    headerName: 'Carrier',
    field: 'carrier',
    minWidth: 220,
    filter: 'agTextColumnFilter',
    cellRenderer: (params: { data: CarrierLaneHistory }) => (
      <Show
        when={params.data.carrierId > 0}
        fallback={<span>{params.data.carrier}</span>}
      >
        <a
          class="text-armstrong-blue font-semibold"
          href={`/Carrier/Details/${params.data.carrierId}`}
          target="_blank"
          rel="noreferrer"
        >
          {params.data.carrier}
        </a>
      </Show>
    ),
  },
  {
    headerName: 'Pickup Date',
    field: 'pickDate',
    minWidth: 120,
    valueFormatter: dateFormatter,
  },
  {
    field: 'origin',
    headerName: 'Origin',
    minWidth: 150,
    sortable: false,
    cellRenderer: (params: { data: CarrierLaneHistory }) =>
      CarrierOriginData(params.data),
  },
  {
    field: 'destination',
    headerName: 'Destination',
    minWidth: 160,
    sortable: false,
    cellRenderer: (params: { data: CarrierLaneHistory }) =>
      CarrierDestinationData(params.data),
  },
  {
    headerName: 'Equipment',
    field: 'equipment',
    minWidth: 130,
    cellRenderer: (params: ICellRendererParams<CarrierLaneHistory>) => (
      <span class="inline-block rounded-full bg-armstrong-light-blue text-armstrong-blue px-2 py-0.5 text-xs whitespace-nowrap">
        {params.data?.equipment}
      </span>
    ),
  },
  {
    headerName: 'Rate',
    field: 'rate',
    minWidth: 80,
    filter: 'agNumberColumnFilter',
    valueFormatter: currencyCellFormatter,
  },
];

const LaneHistory = (props: PropsT) => {
  const lane = createMemo(() => getLoadLane(props.load));
  const modalId = () => `lane-history-${props.load.id}`;

  const [filters, setFilters] = createStore<LaneHistoryFilters>({
    origin: { ...(lane()?.origin ?? EMPTY_LOCATION) },
    originRadiusMiles: DEFAULT_RADIUS_MILES,
    destination: { ...(lane()?.destination ?? EMPTY_LOCATION) },
    destinationRadiusMiles: DEFAULT_RADIUS_MILES,
    equipment: [],
    timeHorizon: DEFAULT_TIME_HORIZON,
    mode: props.load.mode ?? ALL_MODES,
  });
  const [rows, setRows] = createSignal<CarrierLaneHistory[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [lastUpdated, setLastUpdated] = createSignal<DateTime | null>(null);
  const [originEdited, setOriginEdited] = createSignal(false);
  const [destinationEdited, setDestinationEdited] = createSignal(false);
  const [showLocationErrors, setShowLocationErrors] = createSignal(false);
  const locationError = (
    name: string,
    location: LaneLocation,
    edited: boolean,
  ) => {
    if (!showLocationErrors()) {
      return null;
    }
    if (edited) {
      return [`Select ${name.toLowerCase()} from the suggestions`];
    }
    return hasLocation(location) ? null : [`${name} is required`];
  };
  const originError = () =>
    locationError('Origin', filters.origin, originEdited());
  const destinationError = () =>
    locationError('Destination', filters.destination, destinationEdited());
  const [lastSearch, setLastSearch] =
    createSignal<LaneHistorySearchPayload | null>(null);
  const criteriaChanged = createMemo(() => {
    const applied = lastSearch();
    return (
      applied !== null &&
      (originEdited() ||
        destinationEdited() ||
        !isSamePayload(toLaneHistoryPayload(filters), applied))
    );
  });

  createEscapeToClose({
    isOpen: () => isModalOpen(modalId()),
    onEscape: () => closeModal(modalId()),
  });

  let lastSearchedLane: Lane | null = null;

  const resetToLoadLane = () => {
    const currentLane = lane();
    if (!currentLane) {
      return;
    }

    setOriginEdited(false);
    setDestinationEdited(false);
    setShowLocationErrors(false);
    setFilters({
      origin: keepCoordinates(
        currentLane.origin,
        lastSearchedLane?.origin ?? filters.origin,
      ),
      originRadiusMiles: DEFAULT_RADIUS_MILES,
      destination: keepCoordinates(
        currentLane.destination,
        lastSearchedLane?.destination ?? filters.destination,
      ),
      destinationRadiusMiles: DEFAULT_RADIUS_MILES,
    });
  };

  const addEquipment = (value: string) =>
    setFilters('equipment', (current) =>
      current.includes(value) ? current : [...current, value],
    );

  const removeEquipment = (value: string) =>
    setFilters('equipment', (current) =>
      current.filter((item) => item !== value),
    );

  let latestSearchId = 0;

  const runSearch = async (repeat?: LaneHistorySearchPayload) => {
    if (
      !repeat &&
      (!canSearch(filters) || originEdited() || destinationEdited())
    ) {
      setShowLocationErrors(true);
      handleToast(
        ToastType.Error,
        'Pick an origin and a destination from the suggestions before searching.',
      );
      return;
    }

    const searchId = ++latestSearchId;
    const isStale = () => searchId !== latestSearchId;

    setRows([]);
    setLastUpdated(null);
    setLoading(true);
    try {
      let payload = repeat;
      if (!payload) {
        setLastSearch(null);
        const [origin, destination] = await Promise.all([
          resolveLocationCoordinates(filters.origin),
          resolveLocationCoordinates(filters.destination),
        ]);

        if (isStale()) {
          return;
        }

        if (!origin || !destination) {
          handleToast(
            ToastType.Error,
            'Could not locate that origin or destination. Pick one from the suggestions.',
          );
          return;
        }

        lastSearchedLane = { origin, destination };
        setFilters({ origin, destination });

        payload = toLaneHistoryPayload(filters);
        setLastSearch(payload);
      }

      const data = await fetchLaneHistoryData(payload);
      if (isStale()) {
        return;
      }

      setRows(data);
      setLastUpdated(DateTime.local());
    } catch (e) {
      if (!isStale()) {
        handleToast(ToastType.Error, (e as Error).message);
      }
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  };

  const open = () => {
    resetToLoadLane();
    setFilters('mode', props.load.mode ?? ALL_MODES);
    openModal(modalId());

    if (canSearch(filters)) {
      void runSearch();
    }
  };

  const loadContext = () =>
    [
      `From Load #${props.load.loadNum ?? ''}`,
      props.load.hideCustomerName ? '' : customerStore.customer.name,
      props.load.equipment ?? '',
    ]
      .filter(Boolean)
      .join(' · ');

  const header = () => (
    <div>
      <div class="text-xl font-medium">
        Lane History
        <Show
          when={hasLocation(filters.origin) && hasLocation(filters.destination)}
        >
          <MinusIcon size={20} class="inline align-middle mx-1" />
          <span class="underline">{formatLocation(filters.origin)}</span>
          <ArrowRightIcon size={20} class="inline align-middle mx-1" />
          <span class="underline">{formatLocation(filters.destination)}</span>
        </Show>
      </div>
      <div class="text-sm text-gray-500 font-normal mt-1">{loadContext()}</div>
    </div>
  );

  return (
    <>
      <Show when={props.enabled && lane()}>
        <Button
          variant="contained"
          size="sm"
          class="text-armstrong-sm!"
          startIcon={<HistoryIcon />}
          label="Lane History"
          onClick={open}
          dataTestId="lane-history"
        />
      </Show>
      <BasicModal
        id={modalId()}
        width="1100px"
        maxWidth="95vw"
        onClose={() => closeModal(modalId())}
        footer={false}
        customizedHeaderContent={header()}
      >
        <div class="p-4 rounded-sm bg-armstrong-light-blue/50 [&_.select-input-label]:text-primary [&_.select-input-label]:font-semibold [&_.floating-label]:bg-transparent!">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <div>
              <div class="flex gap-2">
                <div class="flex-1">
                  <AutoComplete
                    id="lane-history-origin"
                    label="Origin"
                    endpoint={`capacity/autocomplete?country=${filters.origin.country}&term`}
                    value={formatAutocompleteValue(filters.origin)}
                    onChange={(text) =>
                      setOriginEdited(
                        text !== formatAutocompleteValue(filters.origin),
                      )
                    }
                    onItemSelect={(item) => {
                      setOriginEdited(false);
                      setFilters(
                        'origin',
                        toLaneLocation(item as LocationSelect),
                      );
                    }}
                    onClearValue={() => {
                      setOriginEdited(false);
                      setFilters('origin', clearedLocation(filters.origin));
                    }}
                    renderItem={renderLocationItem}
                    innerRefClass="w-full!"
                    error={originError()}
                    dataTestId="lane-history-origin-input"
                  />
                </div>
                <div class="w-[120px]">
                  <SelectInput
                    label="Country"
                    menuItems={COUNTRY_CODES}
                    value={filters.origin.country}
                    onChange={(country) => {
                      if (country === filters.origin.country) {
                        return;
                      }
                      setOriginEdited(false);
                      setFilters('origin', { ...EMPTY_LOCATION, country });
                    }}
                    dataTestId={formatDataTestId(
                      'Lane history origin country',
                      'selectInput',
                    )}
                  />
                </div>
              </div>
              <RadiusSlider
                label="Origin radius"
                value={filters.originRadiusMiles}
                onChange={(miles) => setFilters('originRadiusMiles', miles)}
                dataTestId="lane-history-origin-radius"
              />
            </div>
            <div>
              <div class="flex gap-2">
                <div class="flex-1">
                  <AutoComplete
                    id="lane-history-destination"
                    label="Destination"
                    endpoint={`capacity/autocomplete?country=${filters.destination.country}&term`}
                    value={formatAutocompleteValue(filters.destination)}
                    onChange={(text) =>
                      setDestinationEdited(
                        text !== formatAutocompleteValue(filters.destination),
                      )
                    }
                    onItemSelect={(item) => {
                      setDestinationEdited(false);
                      setFilters(
                        'destination',
                        toLaneLocation(item as LocationSelect),
                      );
                    }}
                    onClearValue={() => {
                      setDestinationEdited(false);
                      setFilters(
                        'destination',
                        clearedLocation(filters.destination),
                      );
                    }}
                    renderItem={renderLocationItem}
                    innerRefClass="w-full!"
                    error={destinationError()}
                    dataTestId="lane-history-destination-input"
                  />
                </div>
                <div class="w-[120px]">
                  <SelectInput
                    label="Country"
                    menuItems={COUNTRY_CODES}
                    value={filters.destination.country}
                    onChange={(country) => {
                      if (country === filters.destination.country) {
                        return;
                      }
                      setDestinationEdited(false);
                      setFilters('destination', { ...EMPTY_LOCATION, country });
                    }}
                    dataTestId={formatDataTestId(
                      'Lane history destination country',
                      'selectInput',
                    )}
                  />
                </div>
              </div>
              <RadiusSlider
                label="Destination radius"
                value={filters.destinationRadiusMiles}
                onChange={(miles) =>
                  setFilters('destinationRadiusMiles', miles)
                }
                dataTestId="lane-history-destination-radius"
              />
            </div>
          </div>
          <Button
            variant="text"
            size="sm"
            class="text-armstrong-blue!"
            label="Reset to load lane"
            onClick={resetToLoadLane}
            dataTestId={formatDataTestId('Reset to load lane', 'button')}
          />
          <div class="flex flex-wrap items-end gap-4 mt-2">
            <div class="w-[498px]">
              <SelectInputCustom
                label="Equipment"
                placeholder="All Equipment"
                menuItems={equipmentTypes}
                size="sm"
                multiple
                value={filters.equipment}
                renderValue={(selected) => (
                  <Box class="flex flex-wrap gap-1 -ml-2 max-h-[58px] overflow-y-auto">
                    {(selected as string[]).map((value) => (
                      <Chip
                        id={`lane-history-equipment-${value
                          .replace(/[\s,/'"]+/g, '-')
                          .toLowerCase()}`}
                        text={
                          equipmentTypes.find((item) => item.value === value)
                            ?.label ?? value
                        }
                        variant="filled"
                        isDeleteIcon
                        deleteIconColor="white"
                        class="text-white! bg-armstrong-blue! rounded h-[25px]"
                        onDelete={() => removeEquipment(value)}
                      />
                    ))}
                  </Box>
                )}
                onChange={(value, action) =>
                  action === SelectInputCustomOnChangeActionType.Deselect
                    ? removeEquipment(value)
                    : addEquipment(value)
                }
                dataTestId="lane-history-equipment-select"
              />
            </div>
            <div class="w-[180px]">
              <SelectInput
                label="Time Horizon"
                menuItems={TIME_HORIZON_ITEMS}
                value={filters.timeHorizon}
                onChange={(value) => setFilters('timeHorizon', value)}
                dataTestId="lane-history-time-horizon-select"
              />
            </div>
            <div class="w-[120px]">
              <SelectInput
                label="Mode"
                menuItems={MODE_ITEMS}
                value={filters.mode}
                onChange={(value) => setFilters('mode', value)}
                dataTestId="lane-history-mode-select"
              />
            </div>
            <div class="ml-auto">
              <Button
                variant="contained"
                size="sm"
                class="py-5!"
                startIcon={<MagnifyIcon />}
                disabled={loading()}
                label="Search"
                onClick={() => void runSearch()}
                dataTestId={formatDataTestId('Lane History Search', 'button')}
              />
            </div>
          </div>
          <Show when={criteriaChanged()}>
            <div class="flex justify-end mt-2">
              <div
                class="flex items-center gap-3 rounded border border-caution-yellow bg-caution-yellow px-4 py-2"
                role="status"
                data-testid="lane-history-criteria-changed"
              >
                <AlertCircleIcon
                  size={28}
                  class="text-caution-yellow"
                  aria-hidden="true"
                />
                <div>
                  <div class="text-sm font-semibold text-announcement-warning">
                    Criteria changed — results not updated
                  </div>
                  <div class="text-xs text-gray-600">
                    Click SEARCH to apply changes.
                  </div>
                </div>
              </div>
            </div>
          </Show>
        </div>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          gap={1}
          class="mt-3"
        >
          <Show when={lastUpdated()}>
            {(updated) => (
              <span class="text-sm text-gray-500">
                {`Last updated ${updated().toFormat('h:mm:ss a')}`}
              </span>
            )}
          </Show>
          <Button
            variant="text"
            size="sm"
            startIcon={<RefreshIcon />}
            disabled={loading() || lastSearch() === null}
            label="Refresh"
            onClick={() => void runSearch(lastSearch() ?? undefined)}
            dataTestId={formatDataTestId('Lane History Refresh', 'button')}
          />
        </Stack>
        <Show
          when={!loading()}
          fallback={
            <div class="flex justify-center py-10">
              <AtgLoader />
            </div>
          }
        >
          <Show
            when={rows().length > 0}
            fallback={
              <div class="py-10 text-center text-gray-500">
                {lastUpdated()
                  ? 'No loads found on this lane. Try widening the radius or the time horizon.'
                  : 'Search to see carriers who have run this lane.'}
              </div>
            }
          >
            <Box class="ag-theme-alpine h-[420px]">
              <AgGridSolid
                columnDefs={columnDefs}
                rowData={rows()}
                defaultColDef={{ flex: 1, sortable: true, resizable: true }}
                pagination
                paginationPageSize={PAGE_SIZE}
                paginationPageSizeSelector={false}
                getRowStyle={alternateRowStyle}
              />
            </Box>
          </Show>
        </Show>
      </BasicModal>
    </>
  );
};

export default LaneHistory;
